import React, { useEffect, useState } from 'react';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { GridActionsCellItem, GridRenderCellParams } from '@mui/x-data-grid';
import { searchOrganizations, deleteOrganization } from './_api';
import { IOrganization } from './_types';
import { IAddress, ITelecom } from '../_types';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useAlerts from 'components/Alerts/useAlerts';
import { MuiGrid } from 'components/MuiGrid/MuiGrid';
import Header from 'components/Header/Header';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useMuiGrid } from 'utils/hooks/useMuiGrid';
import { useEntityRemove } from 'utils/hooks/useEntityRemove';
import { useActions } from 'utils/hooks/useActions';
import { useUser } from 'utils/hooks/useUser';
import {
  getActiveTranslated,
  getAddressTranslated,
  getIdentifierTranslated,
  getTelecomTranslated,
} from '../_grid';

const muiGridKey = 'organizationsMUI';

const Organizations: React.FC = () => {
  const { t } = useTranslation(['Organizations', 'PatientRegistry']);
  const navigate = useNavigate();
  const { hasRole } = useUser();
  const hasRoleOrganizations = hasRole('ROLE_MANAGE_ORGANIZATIONS');
  const hasMdexRole = hasRole('ROLE_MDEX_VIEW_ADDRESS_BOOK');

  const { toggleLoader } = useAppGlobals();
  const { addErrorAlert } = useAlerts();
  const { storeConfirmationData } = useActions();
  const { confirmationData } = useAppInfo();
  const { injectColumnWidthsIntoColumns, reorderColumnsByGridSettings } = useMuiGrid(muiGridKey);

  const [organizations, setOrganizations] = useState<IOrganization[]>([]);

  const loadOrganizations = async () => {
    toggleLoader();
    try {
      const data = await searchOrganizations({});
      setOrganizations(
        (data || []).map((item: IOrganization) => ({
          ...item,
          activeTranslated: getActiveTranslated(get(item, 'active', false), t),
          identifierTranslated: getIdentifierTranslated(item.identifier as any[]),
          telecomTranslated: getTelecomTranslated(item.telecom as ITelecom[], t),
          addressTranslated: getAddressTranslated(item.address as IAddress[]),
          mdexNodeTranslated: item.mdexNode?.institutionName ?? '',
        })),
      );
    } catch (error) {
      addErrorAlert(t('loadError'));
      console.error('Error loading organizations:', error);
    } finally {
      toggleLoader(false);
    }
  };

  const onCustomEntityEdit = (row: any) => {
    navigate(`//administration/organizations/${row.id}`);
  };

  const { onEntityRemove } = useEntityRemove(deleteOrganization, t, loadOrganizations);

  const clearAction = () => {
    storeConfirmationData(null);
  };
  const confirmAction = async () => {
    await onEntityRemove(get(confirmationData, 'id'));
    clearAction();
  };

  const columns = reorderColumnsByGridSettings(
    injectColumnWidthsIntoColumns(
      [
        {
          field: 'actions',
          headerName: t('Grid:actions'),
          type: 'actions',
          width: 100,
          hideable: false,
          renderCell: ({ row }: GridRenderCellParams) => (
            <>
              <GridActionsCellItem
                icon={
                  <Tooltip title={t('action.edit')}>
                    <EditIcon />
                  </Tooltip>
                }
                label={t('action.edit')}
                onClick={() => onCustomEntityEdit(row)}
                data-pw={`organization-edit-${row.name}`}
                disabled={!hasRoleOrganizations}
              />
              <GridActionsCellItem
                icon={
                  <Tooltip title={t('action.delete')}>
                    <DeleteIcon />
                  </Tooltip>
                }
                label={t('action.delete')}
                onClick={() => {
                  storeConfirmationData({ id: get(row, 'id'), name: get(row, 'name') });
                }}
                data-pw={`organization-delete-${row.name}`}
                disabled={!hasRoleOrganizations}
              />
            </>
          ),
        },
        {
          field: 'activeTranslated',
          headerName: t('PatientRegistry:active'),
          type: 'singleSelect',
          valueOptions: [t('yes'), t('no')],
          width: 120,
        },
        {
          field: 'id',
          headerName: t('PatientRegistry:id'),
          type: 'number',
          width: 100,
        },
        {
          field: 'name',
          headerName: t('name'),
          width: 200,
        },
        {
          field: 'identifierTranslated',
          headerName: t('PatientRegistry:identifier'),
          width: 180,
        },
        {
          field: 'telecomTranslated',
          headerName: t('PatientRegistry:contact'),
          width: 200,
        },
        {
          field: 'addressTranslated',
          headerName: t('PatientRegistry:address'),
          width: 250,
        },
        ...(hasMdexRole
          ? [
              {
                field: 'mdexNodeTranslated',
                headerName: t('mdexNode'),
                width: 200,
              },
            ]
          : []),
      ],
      200,
    ),
  );

  useEffect(() => {
    loadOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Header
        title=""
        addUrl={hasRoleOrganizations ? '/administration//organizations/create' : ''}
      />

      <MuiGrid
        gridKey={muiGridKey}
        rows={organizations}
        columns={columns}
        dataTour="foldersGrid"
        initialSortMode={[{ field: 'name', sort: 'asc' }]}
      />

      {confirmationData && confirmationData.id && !confirmationData.editDialog && (
        <ConfirmationDialog
          title={t('deleteConfirm', { name: confirmationData.name })}
          open={true}
          aria-labelledby="form-dialog-title"
          fullWidth={true}
          cancelAction={clearAction}
          confirmAction={confirmAction}
        />
      )}
    </>
  );
};

export default Organizations;
